import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, X, ArrowRight, AlertTriangle } from 'lucide-react';
import { useSubscription, useBillingNotices } from '@/hooks/useSubscription';

const KEY = 'inkphd-trial-banner-dismissed';

/**
 * Account banner.
 *  - urgent notice (trial ends in ≤3 days / just expired) → always visible, not dismissible
 *  - otherwise a calm trial countdown or free-plan nudge, dismissible for the day
 */
export default function TrialBanner({ urgentOnly = false }) {
  const { sub } = useSubscription();
  const notices = useBillingNotices();
  const [hidden, setHidden] = useState(
    typeof window !== 'undefined' && window.localStorage.getItem(KEY) === new Date().toDateString(),
  );

  const notice = notices[0];
  if (notice) {
    const urgent = notice.severity === 'urgent';
    return (
      <div
        data-testid="trial-notice-banner"
        data-notice-kind={notice.kind}
        className={`mb-8 rounded-xl border px-4 py-3.5 flex flex-wrap items-center gap-x-4 gap-y-2
          ${urgent ? 'border-rose-500/50 bg-rose-500/[0.07]' : 'border-amber-500/45 bg-amber-500/[0.07]'}`}
      >
        <AlertTriangle className={`w-4 h-4 shrink-0 ${urgent ? 'text-rose-600' : 'text-amber-600'}`} />
        <div className="text-[13px] leading-snug flex-1 min-w-[240px]">
          <strong data-testid="trial-notice-title">{notice.title}.</strong> {notice.body}
        </div>
        <Link
          to={notice.cta?.href || '/thesis/billing'}
          data-testid="trial-notice-cta"
          className="text-[12px] font-semibold inline-flex items-center gap-1 rounded-full
            bg-[#0b1a35] hover:bg-[#1d4ed8] text-white px-3.5 py-1.5 transition-colors"
        >
          {notice.cta?.label || 'See plans'} <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    );
  }

  if (urgentOnly || !sub || hidden) return null;

  const trialing = sub.state === 'trialing';
  const free = sub.plan === 'free' && !trialing;
  if (!trialing && !free) return null;

  const dismiss = () => {
    window.localStorage.setItem(KEY, new Date().toDateString());
    setHidden(true);
  };

  const days = sub.trial?.days_left ?? 0;
  const limit = sub.limits?.generations_per_month ?? 0;
  const used = sub.usage?.generations_per_month ?? 0;

  return (
    <div
      data-testid="trial-banner"
      className={`relative mb-8 rounded-xl border px-4 py-3.5 pr-10 flex flex-wrap items-center gap-x-4 gap-y-2
        ${trialing
          ? 'border-amber-500/45 bg-amber-500/[0.07]'
          : 'border-[#1d4ed8]/35 bg-[#1d4ed8]/[0.05]'}`}
    >
      <Clock className={`w-4 h-4 shrink-0 ${trialing ? 'text-amber-600' : 'text-[#1d4ed8]'}`} />
      <div className="text-[13px] leading-snug flex-1 min-w-[240px]">
        {trialing ? (
          <>
            <strong>Free trial · {days} {days === 1 ? 'day' : 'days'} left.</strong>{' '}
            You have full Doctoral access — unlimited projects, defence simulation and
            {limit < 0 ? ' unlimited' : ` ${limit}`} AI generations per month
            {limit > 0 && ` (${used} used)`}. No card required. When the trial ends your
            account moves to the free Starter plan and your work stays intact.
          </>
        ) : (
          <>
            <strong>You are on the free Starter plan.</strong>{' '}
            {limit > 0 && `${used} of ${limit} AI generations used this month. `}
            Upgrade for more sources, DOCX/PDF export and defence readiness scoring.
          </>
        )}
      </div>
      <Link
        to="/thesis/billing"
        data-testid="trial-banner-cta"
        className="text-[12px] font-semibold inline-flex items-center gap-1 rounded-full
          bg-[#0b1a35] hover:bg-[#1d4ed8] text-white px-3.5 py-1.5 transition-colors"
      >
        {trialing ? 'See plans' : 'Upgrade'} <ArrowRight className="w-3 h-3" />
      </Link>
      <button onClick={dismiss} data-testid="trial-banner-dismiss" aria-label="Dismiss"
        className="absolute top-2.5 right-2.5 text-muted-foreground hover:text-foreground">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
